import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Copy, Zap } from "lucide-react";
import { toast } from "sonner";

export const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const lastY = useRef(0);
  const armed = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem("vr_exit_shown")) return;
    const show = () => {
      if (sessionStorage.getItem("vr_exit_shown")) return;
      sessionStorage.setItem("vr_exit_shown", "1");
      setOpen(true);
    };
    const onLeave = (e) => { if (e.clientY <= 0) show(); };
    const onScroll = () => {
      const y = window.scrollY;
      if (y > 1400) armed.current = true;
      if (armed.current && lastY.current - y > 700 && y < 400) show();
      lastY.current = y;
    };
    document.addEventListener("mouseleave", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText("VOLT20");
      toast.success("Code VOLT20 gekopieerd!");
    } catch {
      toast.info("Code: VOLT20");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-[#0b1220]/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}>
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            data-testid="exit-popup"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#1B263B]/95 backdrop-blur-xl p-8 text-center shadow-[0_0_60px_rgba(116,198,157,0.15)]">
            <button data-testid="exit-popup-close" onClick={() => setOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-[background-color,color] duration-200">
              <X className="w-4 h-4" />
            </button>
            <span className="mx-auto w-12 h-12 rounded-full bg-[#74C69D]/15 flex items-center justify-center mb-5">
              <Zap className="w-6 h-6 text-[#74C69D]" />
            </span>
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#74C69D] mb-3">Exclusief voor nieuwe leden</p>
            <h2 className="font-heading font-extrabold text-2xl text-white tracking-tight mb-2">Wacht — pak 20% korting op Pro</h2>
            <p className="text-sm text-white/70 leading-relaxed mb-6">
              De eerste 3 maanden Pro voor <span className="font-mono font-bold text-white">€6,39</span> i.p.v. €7,99 per maand. Gebruik deze code bij het afrekenen:
            </p>
            <button data-testid="exit-popup-copy-code" onClick={copyCode}
              className="w-full flex items-center justify-center gap-3 rounded-2xl border border-dashed border-[#74C69D]/50 bg-[#74C69D]/10 py-4 mb-5 group transition-[background-color] duration-200 hover:bg-[#74C69D]/20">
              <span className="font-mono font-extrabold text-2xl tracking-[0.3em] text-[#74C69D]">VOLT20</span>
              <Copy className="w-4 h-4 text-[#74C69D]/70 group-hover:text-[#74C69D]" />
            </button>
            <Link to="/auth?plan=pro&code=VOLT20" data-testid="exit-popup-cta" onClick={() => setOpen(false)}
              className="block w-full rounded-full bg-primary hover:bg-[#1B4332] text-white py-4 font-bold active:scale-95 transition-[transform,background-color] duration-200">
              Claim je korting
            </Link>
            <button data-testid="exit-popup-dismiss" onClick={() => setOpen(false)}
              className="mt-4 text-xs text-white/40 hover:text-white/70 transition-[color] duration-200">
              Nee bedankt, ik betaal liever de volle prijs
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
